/**
 * Lock Recovery Modal - Modal de recuperação em caso de falha no unlock
 * 
 * Apresentado ao usuário quando:
 * - Falha no desbloqueio após salvamento
 * - Falha no desbloqueio ao fechar modais
 * - Timeout no cleanup do lock
 * 
 * Oferece:
 * - Retry manual de unlock
 * - Force unlock agressivo
 * - Orientações sobre o que fazer
 */

import React, { useState, useCallback } from 'react';
import { AlertTriangle, RefreshCw, Zap, X } from 'lucide-react';
import { lockManager } from '../lib/corel/lockManager';
import { sendToTerminal } from '../lib/terminalLogger';

export interface LockRecoveryModalProps {
  visible: boolean;
  onHide: () => void;
  onRecovered: () => void;
  context?: 'save' | 'checklist' | 'general';
}

export const LockRecoveryModal: React.FC<LockRecoveryModalProps> = ({
  visible,
  onHide,
  onRecovered,
  context = 'general'
}) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const [isForcing, setIsForcing] = useState(false);
  const [retryAttempts, setRetryAttempts] = useState(0);
  const [lastError, setLastError] = useState<string | null>(null);
  const [recoverySuccess, setRecoverySuccess] = useState(false);

  const contextMessages = {
    save: 'durante o salvamento do arquivo',
    checklist: 'ao fechar o checklist',
    general: 'durante uma operação'
  };

  const handleRetryUnlock = useCallback(async () => {
    setIsRetrying(true);
    setLastError(null);
    sendToTerminal('🔄 [RECOVERY] Tentando desbloqueio manual');

    try {
      const result = await lockManager.unlock(5);
      
      if (result.ok) {
        sendToTerminal('✅ [RECOVERY] Desbloqueio bem-sucedido');
        setRecoverySuccess(true);
        setRetryAttempts(prev => prev + 1);
        
        // Aguardar 1s para usuário ver a mensagem de sucesso
        setTimeout(() => {
          onRecovered();
          onHide();
        }, 1500);
      } else {
        sendToTerminal('❌ [RECOVERY] Desbloqueio falhou', {
          error: result.error,
          attempts: result.attempts
        });
        setLastError(result.error || 'Falha ao desbloquear documento');
        setRetryAttempts(prev => prev + 1);
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Erro desconhecido';
      sendToTerminal('❌ [RECOVERY] Exceção ao tentar desbloquear', { error: errorMsg });
      setLastError(errorMsg);
      setRetryAttempts(prev => prev + 1);
    } finally {
      setIsRetrying(false);
    }
  }, [onRecovered, onHide]);

  const handleForceUnlock = useCallback(async () => {
    setIsForcing(true);
    setLastError(null);
    sendToTerminal('⚡ [RECOVERY] Forçando desbloqueio agressivo');

    try {
      const success = await lockManager.forceUnlock();
      
      if (success) {
        sendToTerminal('✅ [RECOVERY] Force unlock bem-sucedido');
        setRecoverySuccess(true);
        
        setTimeout(() => {
          onRecovered();
          onHide();
        }, 1500);
      } else {
        sendToTerminal('⚠️ [RECOVERY] Force unlock não confirmado');
        setLastError('Não foi possível confirmar o desbloqueio. O documento pode ainda estar bloqueado.');
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Erro desconhecido';
      sendToTerminal('❌ [RECOVERY] Exceção no force unlock', { error: errorMsg });
      setLastError(errorMsg);
    } finally {
      setIsForcing(false);
    }
  }, [onRecovered, onHide]);

  const handleIgnore = useCallback(() => {
    sendToTerminal('⚠️ [RECOVERY] Usuário ignorou problema de lock');
    // Reset estado do lock manager (otimista)
    lockManager.resetState();
    onHide();
  }, [onHide]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-navy-800 rounded-xl max-w-2xl w-full border border-yellow-500/30 shadow-2xl">
        <div className="flex flex-col gap-4 p-6">
          {/* Header */}
          <div className="flex items-center gap-3 pb-4 border-b border-navy-700">
            <AlertTriangle className="text-yellow-500 w-8 h-8" />
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-100">
                Documento Bloqueado
              </h2>
              <p className="text-sm text-gray-400">
                Não foi possível desbloquear o documento {contextMessages[context]}
              </p>
            </div>
            <button
              onClick={onHide}
              className="text-gray-400 hover:text-gray-200 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Status */}
          {recoverySuccess ? (
            <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
              <p className="text-green-400 font-medium">
                ✓ Documento desbloqueado com sucesso! Fechando...
              </p>
            </div>
          ) : (
            <>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-yellow-200 text-sm">
                  O documento do CorelDRAW está em modo bloqueado e pode estar impedindo edições. 
                  Tente uma das opções abaixo para resolver.
                </p>
              </div>

              {lastError && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                  <p className="text-red-400 text-sm">
                    <strong>Erro:</strong> {lastError}
                  </p>
                </div>
              )}
            </>
          )}

          {/* Tentativas */}
          {retryAttempts > 0 && !recoverySuccess && (
            <div className="text-sm text-gray-400">
              Tentativas realizadas: <strong className="text-gray-200">{retryAttempts}</strong>
            </div>
          )}

          {/* Ações */}
          <div className="flex flex-col gap-3 pt-2">
            <button
              onClick={handleRetryUnlock}
              disabled={isRetrying || isForcing || recoverySuccess}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:text-gray-500 
                text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center gap-2 
                transition-colors"
            >
              {isRetrying ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Tentando desbloquear...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Tentar Desbloquear Novamente
                </>
              )}
            </button>

            {retryAttempts >= 2 && !recoverySuccess && (
              <button
                onClick={handleForceUnlock}
                disabled={isRetrying || isForcing}
                className="w-full bg-yellow-600 hover:bg-yellow-700 disabled:bg-gray-700 disabled:text-gray-500 
                  text-white font-medium py-3 px-4 rounded-lg flex items-center justify-center gap-2 
                  transition-colors"
              >
                {isForcing ? (
                  <>
                    <Zap className="w-4 h-4 animate-pulse" />
                    Forçando desbloqueio...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4" />
                    Forçar Desbloqueio (Agressivo)
                  </>
                )}
              </button>
            )}

            {!recoverySuccess && (
              <button
                onClick={handleIgnore}
                disabled={isRetrying || isForcing}
                className="w-full bg-transparent border border-gray-600 hover:border-gray-500 
                  disabled:border-gray-700 disabled:text-gray-600 text-gray-300 font-medium py-3 px-4 
                  rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                <X className="w-4 h-4" />
                Ignorar e Continuar
              </button>
            )}
          </div>

          {/* Instruções */}
          <div className="text-sm text-gray-400 bg-navy-900/50 rounded-lg p-4 mt-2 border border-navy-700">
            <p className="font-semibold text-gray-300 mb-2">O que fazer se o problema persistir:</p>
            <ol className="list-decimal list-inside space-y-1">
              <li>Feche este modal e tente reabrir o arquivo no CorelDRAW</li>
              <li>Verifique se não há erros no CorelDRAW (scripts travados)</li>
              <li>Em último caso, feche e reabra o CorelDRAW</li>
              <li>Se o problema continuar, contate o suporte técnico</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

